import { useMemo, useState } from "react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

const RANGES = [
    { key: "30", label: "Last 30 days", days: 30 },
    { key: "90", label: "Last 90 days", days: 90 },
    { key: "all", label: "All time", days: null },
];

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="rounded-lg bg-[#1a0000] border border-[var(--border-accent)] px-3 py-2 shadow-xl">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-champagne)]/70 mb-1">
                {label}
            </p>
            <p className="font-display text-lg font-bold gold-shimmer">
                {new Intl.NumberFormat("en-US").format(payload[0].value)}
            </p>
            <p className="text-[10px] text-[var(--text-secondary)]">followers</p>
        </div>
    );
}

export default function GrowthChart({ history = [] }) {
    const [range, setRange] = useState("all");

    const data = useMemo(() => {
        const r = RANGES.find((x) => x.key === range);
        let points = history.map((h) => ({
            date: new Date(h.recorded_at),
            followers: h.followers,
        }));
        if (r?.days) {
            const cutoff = Date.now() - r.days * 24 * 3600 * 1000;
            points = points.filter((p) => p.date.getTime() >= cutoff);
        }
        return points.map((p) => ({
            label: p.date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            followers: p.followers,
        }));
    }, [history, range]);

    return (
        <div
            data-testid="growth-chart"
            data-glow-catch
            className="rounded-2xl surface-3d p-5 md:p-7"
        >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-[var(--text-primary)]">
                        Follower Growth
                    </h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-1">
                        Organic trajectory over time
                    </p>
                </div>
                <div className="flex gap-1.5 bg-[rgba(0,0,0,0.35)] rounded-full p-1 border border-[var(--border-subtle)]">
                    {RANGES.map((r) => (
                        <button
                            key={r.key}
                            data-testid={`chart-range-${r.key}`}
                            onClick={() => setRange(r.key)}
                            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.15em] transition-colors ${
                                range === r.key
                                    ? "bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold"
                                    : "text-[var(--text-secondary)] hover:text-[var(--accent-champagne)]"
                            }`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="h-64 md:h-80 -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                        <defs>
                            <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#d4af37" stopOpacity={0.5} />
                                <stop offset="60%" stopColor="#d4af37" stopOpacity={0.12} />
                                <stop offset="100%" stopColor="#d4af37" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(243,229,171,0.08)" vertical={false} />
                        <XAxis
                            dataKey="label"
                            tick={{ fill: "rgba(212,197,185,0.6)", fontSize: 11 }}
                            axisLine={{ stroke: "rgba(243,229,171,0.12)" }}
                            tickLine={false}
                            minTickGap={24}
                        />
                        <YAxis
                            tick={{ fill: "rgba(212,197,185,0.6)", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            width={48}
                            tickFormatter={(v) =>
                                v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v
                            }
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "rgba(212,175,55,0.3)" }} />
                        <Area
                            type="monotone"
                            dataKey="followers"
                            stroke="#f3e5ab"
                            strokeWidth={2.5}
                            fill="url(#goldFill)"
                            dot={{ fill: "#d4af37", r: 3, strokeWidth: 0 }}
                            activeDot={{ r: 5, fill: "#ffe08a" }}
                            animationDuration={1200}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            {data.length === 0 && (
                <p className="text-center text-[var(--text-secondary)] py-6 text-sm">
                    No data points in this range yet.
                </p>
            )}
        </div>
    );
}
